import { useEffect, useState, useMemo, useRef } from 'react'
import type { ChangeEvent, FC } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient,  } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { E_ShareholderStatuses } from '@/enums'
import { rqKeys } from '@/react-query-keys'
import { cleanUpPhone, getMs, getShareholderStatusText } from '@/helpers'
import { E_LsKeys } from '@/helpers/ls-keys'
import { useDebounce, useOnlineStatus } from '@/helpers/hooks'
import { useBecome } from '@/helpers/query/use-become'
import { apiRequest, apiRequestNew  } from '@/helpers/request'
import { useAppStore } from '@/store'
import { Button, Drawer, Heading, Input, Checkbox, Modal, Spin, Select } from '@/components'
import { dateMask, formatAnyInputToPhoneMask, phoneMask } from '@/components/input/helpers'
import type { T_AddressLocalitySuggestRes } from '@/types/responses'
import { useBecomeShareholderStore } from '../store'
import {
  SC_FormWrapper,
  SC_CheckboxWrapper,
  SC_DocLink,
} from './styled'

const BECOME_SHAREHOLDER_PENDING_TTL_MS = getMs('10m')


export const BecomeShareholderStep2Drawer: FC = () => {
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()

  const { userData, setUserData } = useAppStore()

  const [ search, setSearch ] = useState('')
  const searchRef = useRef('')
  const isUserTyping = useRef(false)

  const debouncedSearch = useDebounce(search, getMs('300ms'))

  const [ inputs, setInputs ] = useState({
    lastname: '',
    firstname: '',
    patronymic: '',
    mobile: phoneMask,
    birth_date: '',
    locality: '',
  })

  const handleSearch = (value: string) => {
    const wasUserTyping = isUserTyping.current
    setSearch(value)
    isUserTyping.current = true
    if (!value && wasUserTyping) {
      setInputs((prev) => ({
        ...prev,
        locality: '',
      }))
      searchRef.current = ''
    }
  }

  const {
    data: addressList,
    isFetching: isAddressLoading,
  } = useQuery<T_AddressLocalitySuggestRes>({
    queryKey: [ rqKeys.shareholderAddressList, debouncedSearch ],
    queryFn: () =>
      apiRequestNew(
        'GET',
        `/address/locality/suggest?lang=ru&query=${encodeURIComponent(debouncedSearch)}`
      ),
    placeholderData: keepPreviousData,
    staleTime: getMs('1m'),
  })

  const localityOptions = useMemo(() =>
    (Array.isArray(addressList) ? addressList : []).map((item: any) => {
      const fromValue = Array.isArray(item.value)
        ? item.value.filter(Boolean)
        : []

      let label = ''

      if (fromValue.length >= 3) {
        const parts = fromValue.slice(0, 3).reverse()
        label = parts.join(', ')
      } else if (fromValue.length > 0) {
        label = fromValue.reverse().join(', ')
      } else {
        const joinPair = (pair?: [string | null, string | null]) =>
          Array.isArray(pair) && pair[1]
            ? [ pair[0], pair[1] ].filter(Boolean).join(' ').trim()
            : null

        const city = joinPair(item?.city)
        const region = joinPair(item?.region)
        const country = item?.country?.[1] || null

        const parts = [ city, region, country ].filter(Boolean)
        label = parts.join(', ')
      }

      return {
        label,
        value: item.id,
      }
    }), [ addressList ]
  )


  const hideBecomeShareholderStep2Drawer = useBecomeShareholderStore((s) => s.hideBecomeShareholderStep2Drawer)
  const showBecomeShareholderStep3Drawer = useBecomeShareholderStore((s) => s.showBecomeShareholderStep3Drawer)


  const [ checkboxes, setCheckboxes ] = useState<boolean[]>([])
  const [ docName, setDocName ] = useState<string | null>(null)
  const [ update, setUpdate ] = useState(0)
  const { data, isLoading } = useBecome(docName ?? undefined)

  useEffect(() => {
    if (Array.isArray(data)) {
      setCheckboxes((prev) =>
        Array.from({ length: data.length }, (_, i) => prev[i] ?? false)
      )
    }
  }, [ data ])

  const changeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const toggleCheckbox = (index: number) => {
    setCheckboxes((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    )
  }

  const changeSelect = (name: string, value: number | string) => {
    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSelect = (value: number | string) => {
    const selectedOption = localityOptions.find(option => option.value === value)

    if (selectedOption) {
      const label = selectedOption.label
      isUserTyping.current = false
      setSearch(label)
      searchRef.current = label
      setUpdate(prev => prev + 1)
    }
  }

  const handleFocus = () => {
    if (searchRef.current && !search) {
      setSearch(searchRef.current)
    }
  }

  const {
    mutate: requestCallMutation,
    isPending: isRequestCallPending,
  } = useMutation({
    mutationFn: () => apiRequest<any>({
      method: 'client/visitor/edit',
      param: {
        form: 'client/visitor/become_shareholder',
        ...inputs,
        mobile: cleanUpPhone(userData.mobile || inputs.mobile, true),
      },
    }),

    onSuccess: async (res) => {
      if (res.code === 'ok') {
        if (userData.id) {
          localStorage.setItem(
            E_LsKeys.EraBecomeShareholderPendingUserId,
            JSON.stringify({
              userId: userData.id,
              expiresAt: Date.now() + BECOME_SHAREHOLDER_PENDING_TTL_MS,
            })
          )
        }

        setUserData({
          shareholderStatusId: E_ShareholderStatuses.Want,
          shareholderStatus: getShareholderStatusText(E_ShareholderStatuses.Want),
        })
        hideBecomeShareholderStep2Drawer()
        showBecomeShareholderStep3Drawer()
        await queryClient.refetchQueries({ queryKey: [ rqKeys.userData ], type: 'active' })
      } else {
        // Проверяем наличие ошибки не уникального номера телефона
        const mobileError = res?.data?.errors?.mobile
        if (
          mobileError?.code === 'nuniq' ||
          mobileError?.message === '$database.check.mobile.nuniq'
        ) {
          toast('Этот номер телефона уже зарегистрирован', { type: 'error' })
        } else {
          toast('Ошибка отправки заявки', { type: 'error' })
        }
      }
    },
    onError: (error: any) => {
      // Обработка ошибок, которые могут прийти как исключение
      const errorData = error?.response?.data || error
      const mobileError = errorData?.data?.errors?.mobile
      if (
        mobileError?.code === 'nuniq' ||
        mobileError?.message === '$database.check.mobile.nuniq'
      ) {
        toast('Этот номер телефона уже зарегистрирован', { type: 'error' })
      } else {
        toast('Ошибка отправки заявки', { type: 'error' })
      }
    },
  })

  const handleSubmit = () => {
    if (!isOnline) {
      toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
      return
    }

    requestCallMutation()
  }

  return (
    <>
      <Drawer
        isFullScreen
        backTitle='Назад'
        onClose={hideBecomeShareholderStep2Drawer}
      >
        <Heading level={2}>
        Личные данные
        </Heading>

        <p style={{ marginTop: '12px' }}>
        Укажите пожалуйста свои ФИО и адрес регистрации, чтобы мы могли оформить заявление
        </p>

        <SC_FormWrapper>
          <Input
            name='lastname'
            placeholder='Фамилия'
            value={inputs.lastname}
            onChange={changeInput}
            $extraSize='veryLarge'
            $isVeryRadiused
          />

          <Input
            name='firstname'
            placeholder='Имя'
            value={inputs.firstname}
            onChange={changeInput}
            $extraSize='veryLarge'
            $isVeryRadiused
          />

          <Input
            name='patronymic'
            placeholder='Отчество'
            value={inputs.patronymic}
            onChange={changeInput}
            $extraSize='veryLarge'
            $isVeryRadiused
          />

          <Input
            name='mobile'
            value={
              userData.mobile
                ? formatAnyInputToPhoneMask(userData.mobile)
                : inputs.mobile
            }
            onChange={changeInput}
            setFunction={(mobile: string) => {
              setInputs((prev) => ({
                ...prev,
                mobile,
              }))
            }}
            $isPhone
            $extraSize='veryLarge'
            $isVeryRadiused
            disabled={!!userData.mobile}
          />

          <Input
            name='birth_date'
            placeholder='Дата рождения'
            value={inputs.birth_date}
            onChange={changeInput}
            $extraSize='veryLarge'
            $isVeryRadiused
            $isDateMask
          />

          <Select
            key={update}
            name='locality'
            placeholder='Место рождения'
            value={inputs.locality || undefined}
            setFormValue={changeSelect}
            options={localityOptions}
            searchValue={search}
            onSelect={handleSelect}
            onSearch={handleSearch}
            onFocus={handleFocus}
            showSearch
            filterOption={false}
            loading={isAddressLoading}
            notFoundContent={!search.trim()
              ? 'Начните вводить название места рождения'
              : isAddressLoading
                ? 'Поиск…'
                : 'Ничего не найдено'}
          />

        </SC_FormWrapper>

        <SC_CheckboxWrapper>
          {isLoading ? (
            <Spin />
          ) : Array.isArray(data) && data.length ? (
            data.map((doc: any, idx: number) => (
              <Checkbox
                key={doc.id ?? idx}
                checked={checkboxes[idx] ?? false}
                onChange={() => toggleCheckbox(idx)}
              >
                <SC_DocLink
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDocName(doc.name)
                  }}
                >
                  {doc.title?.ru || doc.name}
                </SC_DocLink>
              </Checkbox>
            ))
          ) : (
            <p>Нет документов для согласия.</p>
          )}
        </SC_CheckboxWrapper>

        <Button
          $extraSize='veryLarge'
          $isVeryRadiused
          $isBlack
          block
          onClick={handleSubmit}
          loading={isRequestCallPending}
          disabled={
            isRequestCallPending
          || !inputs.firstname
          || !inputs.lastname
          || !inputs.patronymic
          || !inputs.birth_date
          || inputs.birth_date === dateMask
          || inputs.birth_date.includes('_')
          || !inputs.locality
          || !checkboxes.every(Boolean)
          || !userData.mobile ? (
                !inputs.mobile
              || inputs.mobile === phoneMask
              || inputs.mobile.includes('_')
              )
              : false
          }
        >
        Отправить
        </Button>
      </Drawer>

      <Modal
        open={!!docName}
        onCancel={() => setDocName(null)}
        centered
        destroyOnClose
        footer={null}
        bodyStyle={{
          maxHeight: '70dvh',
          overflowY: 'auto',
          textAlign: 'left',
        }}
      >
        {isLoading ? (
          <Spin />
        ) : (
          <>
            <h3 style={{ marginBottom: 16 }}>{data?.[0].title.ru || docName || ''}</h3>
            {data?.[0].content
              ? <div dangerouslySetInnerHTML={{ __html: data?.[0].content.ru }} />
              : <p>Документ не найден.</p>}
          </>
        )}
      </Modal>

    </>
  )
}
